import { Container, Graphics } from 'pixi.js';
import { isPowerless, type CardDef } from '../../core/card';
import type { CardTitle } from '../../core/ids';
import type { ComboRules } from '../../core/config';
import type { LoreSection } from '../../core/lore';
import { cardDescription, cardLore, cardName } from '../../core/cardText';
import { comboRuleExtras, loreToTagged } from '../../core/lore';
import { INK, TRACKING, destroyWidget, panel, richLabel, uiLine, uiPad, type Panel } from '../ui';

/**
 * **悬停说明** —— 鼠标停在某张手牌上时，在它上方（放不下就下方）弹出的一块说明。
 *
 * 评审：
 *
 * > 还得加一个悬停说明，在牌上方或下方的合适位置显示牌的详细描述
 * > （为每张牌额外添加一个 `lore` 属性，填入此处使用的比描述更长的详细效果说明）。
 *
 * ## 为什么是"另一块面板"而不是把牌面写大
 *
 * `description` 是**印在牌面上**的一句话，牌面上就十来字的位置；而这里要回答的是
 * "什么时候能打、打出去发生什么、什么时候不该打"。信息量差一个数量级，
 * 所以在 `CardDef` 里是**两个字段**（见 `card.ts` 的 `lore`）。
 *
 * ## 位置：**上→下**，不许压住牌自己
 *
 * ```
 *     ╭──────────────╮
 *     │ 牌名          │  ← 优先摆在牌**上方**
 *     ╰──────────────╯
 *          ┌────┐
 *          │ 牌 │          ← 鼠标指着的那一张
 *          └────┘
 *     ╭──────────────╮
 *     │ 牌名          │  ← 上方放不下（牌顶离画布顶太近）才摆到下方
 *     ╰──────────────╯
 * ```
 *
 * 牌在屏幕下半那些位置（正常的手牌）时，"上方"总是够的 —— 这也是为什么默认
 * 是上：说明不该压住牌下面那一排手牌。
 *
 * ⚠️ 摆放是**纯函数**（`tooltipPlacement`），所以"贴边时夹住""放不下就翻到
 *    下面"这两条能在 node 里钉住 —— 靠肉眼只能看到它"好像有点贴边"。
 */

/** 说明面板和牌之间的空隙（逻辑像素） */
export const TOOLTIP_GAP = 10;
/** 说明面板离画布边缘至少留多少 */
export const TOOLTIP_MARGIN = 10;

/** 一张牌的包围盒（逻辑画布坐标） */
export interface TooltipBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface TooltipPlacement {
  readonly x: number;
  readonly y: number;
  /** 摆在牌的哪一边。诊断与测试用 */
  readonly side: 'above' | 'below';
}

/**
 * **说明面板该摆在哪。** 纯函数。
 *
 * 规则（按优先级）：
 * 1. 默认摆在牌**上方**（`y = 牌顶 − 间隙 − 面板高`）
 * 2. 上方越出画布（`y < margin`）就翻到牌**下方**
 * 3. 下方也越出就把 `y` 夹回画布内（面板比画布还高时它会贴顶）
 * 4. 水平**居中于牌**，再夹进画布左右边距里
 */
export function tooltipPlacement(
  card: TooltipBox,
  size: { readonly width: number; readonly height: number },
  bounds: { readonly width: number; readonly height: number },
  gap: number = TOOLTIP_GAP,
  margin: number = TOOLTIP_MARGIN,
): TooltipPlacement {
  const above = card.y - gap - size.height;
  const below = card.y + card.height + gap;
  let side: 'above' | 'below' = 'above';
  let y = above;
  if (above < margin) {
    side = 'below';
    y = below;
  }
  const maxY = Math.max(margin, bounds.height - size.height - margin);
  y = Math.min(maxY, Math.max(margin, y));

  const centered = card.x + card.width / 2 - size.width / 2;
  const maxX = Math.max(margin, bounds.width - size.width - margin);
  const x = Math.min(maxX, Math.max(margin, centered));
  return { x, y, side };
}

/**
 * **谁被指着** —— 说明面板只问这两个问题。
 *
 * ⚠️ 做成结构类型（而不是 import 那两个类）是刻意的：**手牌**和**浮在正中的
 *    那张牌**都满足它（`Hand.hoveredDef` / `Hand.hoveredBox()`、
 *    `CenteredCard.hoveredDef` / `CenteredCard.hoveredBox()`），于是说明面板
 *    一行都不用改就同时服务两边（评审："不要重复实现功能"）。
 */
/**
 * **全局包围盒 → 某个容器坐标系里的包围盒。**
 *
 * 游戏世界整体被 `world.scale` 缩放（见 `ui/screenHost.ts`），所以
 * `Container.getBounds()` 给的是**世界/屏幕坐标**，而说明面板的
 * `#bounds` 与手牌给的盒子都是**逻辑画布坐标**。记牌器表头 / 抽牌堆里的牌
 * 是深层子节点（还带遮罩、滚动），必须先把 `getBounds()` 换算回逻辑坐标，
 * 否则说明会跑到很远的错误位置。
 *
 * 传入 `toLocal` 而不是容器，是为了能在 node 里钉住这条换算（纯函数）。
 */
export function toLocalBox(
  toLocal: (point: { readonly x: number; readonly y: number }) => { readonly x: number; readonly y: number },
  box: TooltipBox,
): TooltipBox {
  const a = toLocal({ x: box.x, y: box.y });
  const b = toLocal({ x: box.x + box.width, y: box.y + box.height });
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
  };
}

export interface HoverSource {
  readonly hoveredDef: CardDef | null;
  hoveredBox(): { readonly x: number; readonly y: number; readonly width: number; readonly height: number } | null;
}

/**
 * **一帧同步一次：被指着的那张牌，说明就跟着摆到它旁边。**
 *
 * 两个对局屏（热座 / 联机）都要做这件事，所以收成一处：
 *
 * ```
 *   def = source.hoveredDef      ← 那块说明要显示哪张牌（没指着就是 null）
 *   box = source.hoveredBox()    ← 那张牌在画布上的包围盒（定位 + 夹边）
 * ```
 *
 * ⚠️ 必须在 `update()` **之后**调用：hover 是那一帧的命中测试算出来的。
 * ⚠️ "浮在正中那张牌"和"手牌"同时被指着时，调用方要**先问前者**
 *    （它更稀有、也更重要；手牌在下面，两者一般不会重叠）。
 */
/** 悬停一个**动作按钮**时要显示的那句话（牌没被指着时用） */
export interface TooltipTip {
  readonly title: string;
  readonly text: string;
  readonly box: TooltipBox;
}

/**
 * 一帧同步一次：优先显示**被指着的牌**；没有牌、但有动作按钮的说明时，
 * 显示动作说明。两者**共用同一块面板**（标题 + 正文 + 同一个浮现动画），
 * 所以按钮的 hover 和牌的 hover 长得一模一样。
 */
export function syncCardTooltip(
  tooltip: CardTooltip,
  source: HoverSource,
  dtMs: number,
  tip: TooltipTip | null = null,
): void {
  const def = source.hoveredDef;
  const box = def === null ? null : source.hoveredBox();
  if (def !== null && box !== null) tooltip.showCard(def, box);
  else if (tip !== null) tooltip.showTip(tip.title, tip.text, tip.box);
  else {
    tooltip.hide();
    return;
  }
  // ★ 推进"向上浮现"那一小段（150ms）。⚠️ 必须每帧调：动画期间位置也要跟着牌走
  tooltip.update(dtMs);
}

export interface CardTooltipOptions {
  /** UI 倍率（`uiPad` / 字号都从它走） */
  readonly scale: number;
  /** 画布尺寸。用来夹边 */
  readonly bounds: { readonly width: number; readonly height: number };
  /** 面板宽度（设计尺寸，会乘倍率） */
  readonly width?: number;
  /**
   * **牌引用怎么取名字**：lore 里的 `[捕获]` 存的是 id，显示名要按当前语言解析。
   * 不给就退化成“去掉包前缀的 id”，方便调试页/测试直接用。
   */
  readonly cardNameOf?: (id: CardTitle) => string;
  /**
   * **当前房间的连击村规。** 无效牌的悬停说明会按它是否启用，额外插几段
   * "功能：一对 / 三条 / 五张不同名"（见 `core/lore.ts` 的 `comboRuleExtras`）。
   * 不给就不显示（调试页/测试）。
   */
  readonly comboRules?: () => ComboRules;
  /**
   * **玩家在设置里勾选了哪些说明板块**（`ui/settings.ts` 的 `loreSections`）。
   * 不给就全显示（测试 / 调试页）。无效牌的连击条目挂在 `effect` 板块下，
   * 所以关掉「功能」它们也一起消失（见 `core/lore.ts` 的 `loreToTagged`）。
   */
  readonly loreSections?: () => readonly LoreSection[];
}

/** 浮现动画多久（毫秒）。评审："短暂（150ms 左右）" */
const TOOLTIP_APPEAR_MS = 150;
/** 浮现时从多低升上来（逻辑像素）。正值 = 从牌那边往下一点开始 */
const TOOLTIP_APPEAR_RISE = 9;

/**
 * **一块悬停说明面板。**
 *
 * 用法：每帧（或 hover 变了的时候）调 `show(def, 牌的包围盒)`；不指着任何牌时
 * `hide()`。内容变了才重建文字 —— `Text` 重建一次要量字形，别每帧来一遍。
 */
export class CardTooltip {
  /** 加进场景的东西。**要加在牌那一层之上** */
  readonly container: Container;

  readonly #scale: number;
  readonly #bounds: { width: number; height: number };
  readonly #width: number;
  readonly #cardNameOf: (id: CardTitle) => string;
  readonly #comboRules: (() => ComboRules) | null;
  readonly #loreSections: (() => readonly LoreSection[]) | null;

  #panel: Panel | null = null;
  /** 上一次 `show()` 摆在哪一边（诊断与冒烟用） */
  #side: 'above' | 'below' = 'above';
  /** 现在展示的是哪张牌（`id`）。同一张牌不重建 */
  #shown: string | null = null;
  /** 上次重建时用的板块签名 —— 玩家在设置里改了勾选，即使牌没换也要重建 */
  #shownSig = '';
  #visible = false;
  /**
   * **浮现动画走到哪儿了**（毫秒）。换一张牌就从头开始。
   *
   * 评审：「hover 卡牌时的悬浮窗要有个向上浮现的短暂（150ms 左右）的动画
   * （y-offset 从较小的正值变为 0，opacity 从 0 变 1）」。
   */
  #appearMs = 0;
  /** 这一帧摆在哪张牌旁边（`update` 每帧重摆要用） */
  #card: TooltipBox | null = null;

  constructor(options: CardTooltipOptions) {
    this.#scale = options.scale;
    this.#bounds = { width: options.bounds.width, height: options.bounds.height };
    this.#width = options.width ?? 300;
    this.#cardNameOf =
      options.cardNameOf ?? ((id: CardTitle): string => String(id).replace(/^[^:]+:/, ''));
    this.#comboRules = options.comboRules ?? null;
    this.#loreSections = options.loreSections ?? null;
    this.container = new Container();
    this.container.label = 'card-tooltip-layer';
    this.container.eventMode = 'none';
    this.container.visible = false;
    // ⚠️ 说明是"浮在最上面的一张纸"，所以它自己那一层必须有很高的 zIndex ——
    //    手牌里的牌会在排序时把 zIndex 顶到 3000（见 handView），
    //    这一层不进那套排序，靠**挂在更后面的层**来压住它们（见 game.ts）
    this.container.zIndex = 10000;
  }

  /** 现在显示着吗 */
  get visible(): boolean {
    return this.#visible;
  }

  /** 现在展示的是哪张牌（`CardDef.id`）。没显示就是 `null` */
  get showing(): string | null {
    return this.#shown;
  }

  /** 现在摆在牌的哪一边。诊断与冒烟用 */
  get side(): 'above' | 'below' {
    return this.#side;
  }

  /** 面板现在多大（逻辑像素）。诊断与冒烟用 */
  get size(): { readonly width: number; readonly height: number } {
    const panel_ = this.#panel;
    return panel_ === null ? { width: 0, height: 0 } : { width: panel_.width, height: panel_.height };
  }

  /**
   * **显示某张牌的说明。**
   *
   * `card` 是那张牌在**逻辑画布坐标**里的包围盒（手牌给的是它自己的四个角，
   * 见 `game.ts` 里的接法）。同一张牌重复调用只挪位置，不重建文字。
   */
  showCard(def: CardDef, card: TooltipBox): void {
    const sig = this.#loreSections === null ? '' : this.#loreSections().join(',');
    if (this.#shown !== def.id || this.#panel === null || this.#shownSig !== sig) {
      this.#rebuild(def);
      this.#shownSig = sig;
      // ★ 换了一张牌 / 改了板块：浮现动画从头来一遍（同一状态重复 show 不重放）
      this.#appearMs = 0;
    }
    this.#placeAt(card);
  }

  /**
   * **显示一句动作说明**（动作按钮的 hover）。
   *
   * 走的是和 `showCard` 完全相同的面板与定位 —— 这是刻意的：按钮的说明
   * 不该长成另一种悬浮纸。`title` 是按钮上的字（"结束回合"…）。
   */
  showTip(title: string, text: string, box: TooltipBox): void {
    const key = 'tip:' + title + '|' + text;
    if (this.#shown !== key || this.#panel === null) {
      this.#rebuildTip(title, text);
      this.#appearMs = 0;
    }
    this.#placeAt(box);
  }

  #placeAt(box: TooltipBox): void {
    if (this.#panel === null) return;
    this.#card = box;
    this.container.visible = true;
    this.#visible = true;
    this.#place(box);
  }

  /**
   * **推进浮现动画**（每帧一次，`syncCardTooltip` 里调）。
   *
   * 形状是"**向上浮现**"：y 从一个小的正值升到 0（ease-out），同时 alpha 0 → 1。
   * 总时长 `TOOLTIP_APPEAR_MS`（150ms）。
   *
   * ⚠️ 动画期间位置**每帧重算**：牌在动（手牌有摆动、让位）的时候，说明面板要
   *    跟着走，不能只在 `show()` 里摆一次。
   */
  update(dtMs: number): void {
    if (!this.#visible) return;
    this.#appearMs = Math.min(TOOLTIP_APPEAR_MS, this.#appearMs + Math.max(0, dtMs));
    if (this.#card !== null) this.#place(this.#card);
  }

  /** 摆到牌的旁边，并叠上浮现动画的位移与不透明度 */
  #place(card: TooltipBox): void {
    const panel_ = this.#panel;
    if (panel_ === null) return;
    const at = tooltipPlacement(card, { width: panel_.width, height: panel_.height }, this.#bounds);
    const k = TOOLTIP_APPEAR_MS <= 0 ? 1 : this.#appearMs / TOOLTIP_APPEAR_MS;
    // 位移用 ease-out（先快后慢），透明度线性 —— 读起来是"浮上来"而不是"淡进来"
    const eased = 1 - (1 - k) ** 3;
    this.container.position.set(at.x, at.y + TOOLTIP_APPEAR_RISE * (1 - eased));
    this.container.alpha = k;
    this.#side = at.side;
  }

  hide(): void {
    this.container.visible = false;
    this.container.alpha = 1;
    this.#visible = false;
    this.#card = null;
    this.#appearMs = TOOLTIP_APPEAR_MS;
  }

  /** 画布尺寸变了（窗口变了）。位置下一帧 `show()` 会跟着重算 */
  resize(bounds: { readonly width: number; readonly height: number }): void {
    this.#bounds.width = bounds.width;
    this.#bounds.height = bounds.height;
  }

  destroy(): void {
    if (this.#panel !== null) destroyWidget(this.#panel);
    this.#panel = null;
    this.container.destroy({ children: true });
  }

  #rebuild(def: CardDef): void {
    if (this.#panel !== null) destroyWidget(this.#panel);

    const panel_ = panel({
      scale: this.#scale,
      width: uiPad(this.#width, this.#scale),
      title: cardName(def),
    });
    /** 内容宽度：面板宽减去左右内边距（`panel()` 自己会加内边距，这里只算文字区） */
    const inner = uiPad(this.#width - 24, this.#scale);

    // 一句"印在牌面上的"短描述放最上面（低对比）——悬停时先对上是哪张牌
    const short = richLabel({
      text: cardDescription(def),
      scale: this.#scale,
      size: 11,
      fill: INK.textDim,
      role: 'cjk',
      wrapWidth: inner,
    });
    panel_.body.addChild(short);

    let y = short.height + uiPad(8, this.#scale);
    // 一条分隔线：短描述和详细说明是两种语气，靠线分开（不靠投影，见 ui-design）
    const rule = new Graphics()
      .rect(0, 0, inner, uiLine(1, this.#scale))
      .fill({ color: INK.edge, alpha: 0.6 });
    rule.position.set(0, y);
    panel_.body.addChild(rule);
    y += uiPad(9, this.#scale);

    // ★ 无效牌：把当前房间启用的连击规则插在"功能"之后（它不是这张牌的数据）
    const extras =
      this.#comboRules !== null && isPowerless(def) ? comboRuleExtras(this.#comboRules()) : '';
    const sections = this.#loreSections === null ? null : this.#loreSections();
    const visible =
      sections === null ? undefined : (section: LoreSection): boolean => sections.includes(section);
    const long = richLabel({
      text: loreToTagged(
        cardLore(def),
        this.#cardNameOf,
        extras === '' ? undefined : { after: 'effect', tagged: extras },
        visible,
      ),
      scale: this.#scale,
      size: 12,
      fill: INK.text,
      role: 'cjk',
      wrapWidth: inner,
      tracking: TRACKING.normal,
    });
    long.position.set(0, y);
    panel_.body.addChild(long);
    y += long.height;

    panel_.fitContent(y);

    this.#panel = panel_;
    this.#shown = def.id;
    this.container.addChild(panel_.container);
  }

  /** 动作说明：**标题 + 一句正文**。和牌的说明同一个面板样式（只有标题栏和正文） */
  #rebuildTip(title: string, text: string): void {
    if (this.#panel !== null) destroyWidget(this.#panel);
    const panel_ = panel({
      scale: this.#scale,
      width: uiPad(this.#width, this.#scale),
      title,
    });
    const inner = uiPad(this.#width - 24, this.#scale);
    const body = richLabel({
      text,
      scale: this.#scale,
      size: 12,
      fill: INK.text,
      role: 'cjk',
      wrapWidth: inner,
      tracking: TRACKING.normal,
    });
    panel_.body.addChild(body);
    panel_.fitContent(body.height);
    this.#panel = panel_;
    this.#shown = 'tip:' + title + '|' + text;
    this.container.addChild(panel_.container);
  }
}
